import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Sparkles, Battery, Moon, UtensilsCrossed, Brain } from 'lucide-react';

const QuizInteraction = ({ onComplete }) => {
  const [selectedFeeling, setSelectedFeeling] = useState(null);
  const [showResult, setShowResult] = useState(false);

  const feelings = [
    { 
      id: 'low-energy', 
      icon: Battery, 
      label: 'Low Energy',
      emoji: '😴',
      message: 'Your body may be craving essential nutrients. A natural cleanse can restore your vitality.' 
    },
    { 
      id: 'poor-sleep', 
      icon: Moon, 
      label: 'Poor Sleep',
      emoji: '💤',
      message: 'Toxin buildup can disrupt sleep cycles. A cleanse can help restore your natural rhythm.' 
    },
    { 
      id: 'bloating', 
      icon: UtensilsCrossed, 
      label: 'Bloating / Indigestion',
      emoji: '🍽️',
      message: 'Digestive stress is a sign your gut needs renewal. Let\'s reset your system naturally.' 
    },
    { 
      id: 'brain-fog', 
      icon: Brain, 
      label: 'Brain Fog / Stress',
      emoji: '🧠',
      message: 'Mental clarity comes from physical wellness. A detox can clear the fog and restore focus.' 
    },
  ];

  const handleSelect = (feeling) => {
    setSelectedFeeling(feeling);
    setTimeout(() => {
      setShowResult(true);
      if (onComplete) {
        onComplete(feeling);
      }
    }, 500);
  };

  return (
    <div className="py-16 px-4 bg-gradient-to-br from-purple-50 to-white">
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
            How Are You Feeling Lately?
          </h2>
          <p className="text-lg text-gray-600">
            Let's understand your body's signals
          </p>
        </motion.div>

        {!showResult ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {feelings.map((feeling, index) => {
              const Icon = feeling.icon;
              return (
                <motion.button
                  key={feeling.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => handleSelect(feeling)}
                  className={`relative p-6 rounded-2xl border-2 transition-all duration-300 ${
                    selectedFeeling?.id === feeling.id
                      ? 'border-primary bg-primary/10 shadow-lg'
                      : 'border-gray-200 bg-white hover:border-primary/50 hover:shadow-md'
                  }`}
                >
                  <div className="text-4xl mb-3">{feeling.emoji}</div>
                  <Icon className="w-8 h-8 text-primary mx-auto mb-3" />
                  <p className="text-sm md:text-base font-semibold text-gray-800">
                    {feeling.label}
                  </p>
                </motion.button>
              );
            })}
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="max-w-2xl mx-auto"
          >
            <div className="relative bg-gradient-to-br from-primary/10 via-purple-50 to-accent rounded-3xl p-8 md:p-12 shadow-xl overflow-hidden">
              {/* Glow effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-primary/20 to-purple-300/20 blur-3xl opacity-50" />
              
              <div className="relative z-10">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, type: 'spring' }}
                  className="w-20 h-20 bg-primary rounded-full flex items-center justify-center mx-auto mb-6"
                >
                  <Sparkles className="w-10 h-10 text-white" />
                </motion.div>

                <h3 className="text-2xl md:text-3xl font-bold text-gray-800 mb-4 text-center">
                  Your Body is Speaking to You
                </h3>
                
                <p className="text-lg text-gray-700 text-center mb-8 leading-relaxed">
                  {selectedFeeling?.message}
                </p>

                <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 mb-6">
                  <p className="text-center text-gray-800 font-medium mb-4">
                    ✨ Personalized Cleanse Plan Ready
                  </p>
                  <div className="flex flex-wrap justify-center gap-3 text-sm text-gray-600">
                    <span className="bg-green-100 text-green-800 px-4 py-2 rounded-full">
                      🌿 100% Natural
                    </span>
                    <span className="bg-blue-100 text-blue-800 px-4 py-2 rounded-full">
                      ⏱️ 16 Hours
                    </span>
                    <span className="bg-purple-100 text-purple-800 px-4 py-2 rounded-full">
                      ✅ Certified Therapists
                    </span>
                  </div>
                </div>

                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => {
                    const bookingSection = document.getElementById('booking-section');
                    bookingSection?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="w-full bg-primary text-white py-4 rounded-xl text-lg font-semibold shadow-lg hover:shadow-xl transition-all"
                >
                  Start My Detox Journey →
                </motion.button>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default QuizInteraction;
